import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { render as renderRTL } from '@testing-library/react';
import { graphql, HttpResponse } from 'msw';
import { useMemo, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {
  cacheExchange,
  createClient,
  fetchExchange,
  Provider as UrqlProvider,
} from 'urql';

import { Role } from '@revelio/data-access';
import { theme } from '@revelio/styles';

import {
  CLIENT_USER_DETAILS_ID,
  clientUserDetailsQueryData,
  userDetailsQueryData,
} from './userDetailsQuery';

interface DataProviderProps {
  children: React.ReactNode;
  routerProps?: { initialEntries?: string[] };
}

const DataProviders = ({ children, routerProps }: DataProviderProps) => {
  const initialEntry = routerProps?.initialEntries?.[0];
  const didSetInitialEntryRef = useRef(false);

  if (initialEntry && !didSetInitialEntryRef.current) {
    window.history.pushState({}, '', initialEntry);
    didSetInitialEntryRef.current = true;
  }

  const urqlClient = useMemo(
    () =>
      createClient({
        url: '/',
        exchanges: [cacheExchange, fetchExchange],
      }),
    []
  );

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false },
          mutations: { retry: false },
        },
      }),
    []
  );

  return (
    <UrqlProvider value={urqlClient}>
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme} portalZIndex={40}>
          <BrowserRouter>{children}</BrowserRouter>
        </ChakraProvider>
      </QueryClientProvider>
    </UrqlProvider>
  );
};

export const renderWithData = (
  ui: JSX.Element,
  options?: Omit<DataProviderProps, 'children'>
): ReturnType<typeof renderRTL> => {
  return {
    ...renderRTL(ui, {
      wrapper: ({ children }) => (
        <DataProviders {...options}>{children}</DataProviders>
      ),
    }),
  };
};

export const GetRevelioAdminUserDetailsHandler = graphql.query(
  'GetUserDetails',
  ({ variables }) => {
    const { ids } = variables;
    if (ids[0] === CLIENT_USER_DETAILS_ID) {
      return HttpResponse.json({ data: clientUserDetailsQueryData() });
    }
    return HttpResponse.json({
      data: userDetailsQueryData({ role: Role.RevelioAdmin }),
    });
  }
);

export const GetClientAdminUserDetailsHandler = graphql.query(
  'GetUserDetails',
  () => {
    return HttpResponse.json({
      data: userDetailsQueryData({ role: Role.ClientAdmin }),
    });
  }
);

export const GetClientUserDetailsHandler = graphql.query(
  'GetUserDetails',
  () => {
    return HttpResponse.json({
      data: userDetailsQueryData({ role: Role.ClientUser }),
    });
  }
);

export const GetTrialUserDetailsHandler = graphql.query(
  'GetUserDetails',
  () => {
    return HttpResponse.json({
      data: userDetailsQueryData({ role: Role.ClientUser, live: false }),
    });
  }
);
